import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '@/services/api';
import type { Applicant } from '@/services/types';

interface ApplicantState {
  applicants: Applicant[];
  current: string;
  setCurrent: (id: string) => void;
  refresh: () => Promise<void>;
  loading: boolean;
}

const ApplicantCtx = createContext<ApplicantState | null>(null);
const STORAGE_KEY = 'jobapplier_admin_applicant';

export function ApplicantProvider({ children }: { children: ReactNode }) {
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [current, setCurrentState] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? 'peter';
    } catch {
      return 'peter';
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get<{ applicants: Applicant[] }>('/applicants');
      setApplicants(r.applicants);
      if (!r.applicants.find((a) => a.applicant_id === current) && r.applicants[0]) {
        setCurrentState(r.applicants[0].applicant_id);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const setCurrent = (id: string) => {
    setCurrentState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  };

  return (
    <ApplicantCtx.Provider value={{ applicants, current, setCurrent, refresh, loading }}>
      {children}
    </ApplicantCtx.Provider>
  );
}

export function useApplicants() {
  const ctx = useContext(ApplicantCtx);
  if (!ctx) throw new Error('useApplicants must be used inside <ApplicantProvider>');
  return ctx;
}
